import React from 'react';
import { ExposureConfigurationResponse } from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { CurrencyCode } from '../../../types/currencyCode';
import { EndorsementExposureUpdateRequest } from '../../../types/endorsements';
import {
	Exposure,
	ExposureCreateRequest,
	PolicyCharacteristics,
} from '../../../types/policy';
import { filterForPolicySnapshot } from '../util_sharedEndorsementAndRenewal';
import AddedExposureDetails from './AddedExposureDetails';
import EndedExposureDetails from './EndedExposureDetails';
import UpdatedExposureDetails from './UpdatedExposureDetails';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	policyCharacteristic: PolicyCharacteristics;
	characteristicTimestamp: string;
	referenceTimestamp: string;
	exposures: Exposure[];
	addedExposures: ExposureCreateRequest[];
	updatedExposures: EndorsementExposureUpdateRequest[]; // this can also be RenewalExposureUpdateRequest[]
	endedExposures: string[];
	exposureConfigurations: ExposureConfigurationResponse[];
	currency: CurrencyCode;
}

const ExposureChanges = (props: Props) => {
	const {
		policyCharacteristic,
		characteristicTimestamp,
		referenceTimestamp,
		exposures,
		addedExposures,
		updatedExposures,
		endedExposures,
		exposureConfigurations,
		currency,
	} = props;

	const hasAddedExposures = addedExposures && addedExposures.length;
	const hasUpdatedExposures = updatedExposures && updatedExposures.length;
	const hasEndedExposures = endedExposures && endedExposures.length;
	if (!hasAddedExposures && !hasUpdatedExposures && !hasEndedExposures) {
		return null;
	}

	const renderUpdatedExposures = () => {
		if (!updatedExposures.length) {
			return null;
		}
		return updatedExposures.map((e) => {
			const exposure = exposures.find((ex) => ex.locator === e.exposureLocator);

			const exposureChar = exposure.characteristics
				.filter((c) =>
					filterForPolicySnapshot(
						c.createdTimestamp,
						c.replacedTimestamp,
						referenceTimestamp,
					),
				)
				.find(
					(char) =>
						parseInt(char.startTimestamp, 10) <=
							parseInt(characteristicTimestamp, 10) &&
						parseInt(char.endTimestamp, 10) >=
							parseInt(characteristicTimestamp, 10),
				);
			const exposureConfig = exposureConfigurations.find(
				(config) => config.name === exposure.name,
			);
			return (
				<UpdatedExposureDetails
					key={e.exposureLocator}
					characteristicTimestamp={characteristicTimestamp}
					referenceTimestamp={referenceTimestamp}
					exposure={exposure}
					exposureCharacteristic={exposureChar}
					updatedExposure={e}
					exposureConfiguration={exposureConfig}
					policyCharacteristic={policyCharacteristic}
				/>
			);
		});
	};

	const renderEndedExposures = () => {
		if (!endedExposures.length) {
			return null;
		}
		return endedExposures.map((locator) => {
			const exposure = exposures.find((ex) => ex.locator === locator);
			const exposureChar = exposure.characteristics
				.filter((c) =>
					filterForPolicySnapshot(
						c.createdTimestamp,
						c.replacedTimestamp,
						referenceTimestamp,
					),
				)
				.find(
					(char) =>
						parseInt(char.startTimestamp, 10) <=
							parseInt(characteristicTimestamp, 10) &&
						parseInt(char.endTimestamp, 10) >=
							parseInt(characteristicTimestamp, 10),
				);
			const exposureConfig = exposureConfigurations.find(
				(config) => config.name === exposure.name,
			);
			return (
				<EndedExposureDetails
					key={locator}
					characteristicTimestamp={characteristicTimestamp}
					referenceTimestamp={referenceTimestamp}
					exposure={exposure}
					exposureCharacteristic={exposureChar}
					exposureConfiguration={exposureConfig}
				/>
			);
		});
	};

	return (
		<div style={{ marginTop: '5px' }}>
			{renderUpdatedExposures()}
			<AddedExposureDetails
				policyCharacteristic={policyCharacteristic}
				addedExposures={addedExposures}
				exposureConfigurations={exposureConfigurations}
				currency={currency}
			/>
			{renderEndedExposures()}
		</div>
	);
};

export default withPolicyContext<Props>(ExposureChanges);
